'use client'
import type {
  ForgotFormValues,
} from './constants'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'

import { useForm } from 'react-hook-form'
import { Link, useRouter } from '~/i18n/routing'
import { routes } from '~/lib/constants'
import useForgot from '~/lib/hooks/auth/use-forgot'
import { sleep } from '~/lib/utils'
import { Button } from '~/modules/common/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from '~/modules/common/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/modules/common/components/ui/form'
import { Input } from '~/modules/common/components/ui/input'

import { toast } from '~/modules/common/components/ui/use-toast'
import { TypographyH1 } from '../../common/components/typography/typography'
import {
  getForgotFormSchema,
  getInitialDefaultValues,
} from './constants'

export function ForgotForm() {
  const t = useTranslations('page.forgot')
  const router = useRouter()
  const { mutateAsync: sendForgotRequest } = useForgot()

  const forgotFormSchema = getForgotFormSchema({ t })
  const form = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotFormSchema),
    defaultValues: getInitialDefaultValues({}),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const handleForgotPassword = async (data: ForgotFormValues) => {
    try {
      await sendForgotRequest(data)
      toast({ title: t('successMessage') })
      await sleep(1000)
      router.push(routes.login)
    }
    catch (error) {
      const err = error as APIError
      if (err?.error?.code !== 500) {
        toast({
          title: err.message || t('errorMessage'),
          variant: 'destructive',
        })
      }
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleForgotPassword)}
        className="space-y-8 max-w-[440px]"
      >
        <Card className="mx-auto max-w-sm">
          <CardHeader>
            <TypographyH1>{t('title')}</TypographyH1>
            <CardDescription>{t('description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.inputs.email.label')}</FormLabel>
                      <FormControl>
                        <Input
                          id="email"
                          placeholder="m@example.com"
                          type="email"
                          required
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button type="submit" className="w-full">
                {t('submitButton')}
              </Button>
            </div>
            <div className="mt-4 text-center text-sm">
              {t('rememberPassword')}
              {' '}
              <Link href={routes.login} className="underline">
                {t('loginInstead')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  )
}
